import { HyDryEventLog } from "./HyDryEventLog";
import { getHyDryEventNumber, HyDryEvent } from "./HyDryEvent";
import { calculateIndexFromSlotNumber } from "./ComBox";
import { ComBoxSensorType } from "./ComBoxSensorData";
import { formatSensorLegendBatteryStatus } from "@/utils/SensorLegendBatteryFormatter";

/**
 * Per-sensor metadata for display in the drying graph legend.
 *
 * Extracted from the event 77 (SENSOR_CONFIGURATION_CHANGED) **snapshot** entries, sub-type 10.
 * Identity and diagnostics only: the sensor's warning state comes from the ComBox's live flags
 * (`getSensorWarningsFromFlags` in `ComBox.ts`), never from the log. The warning sub-types are
 * still written by the Cloud and still readable in the Logbuch; they are simply not a source of
 * state for any screen.
 */
export interface SensorLegendMetadata {
  sensorIndex: number;
  slotNumber: number;
  serialNumber?: string;
  firmwareVersion?: string;
  batteryLevel?: string;
  rssi?: string;
  /** Sensor hardware type from sensorConfig. Set by the UI when opening the info dialog. */
  sensorType?: ComBoxSensorType;
}

export { formatSensorLegendBatteryStatus };

function getOrCreateEntry(
  metadata: Map<number, SensorLegendMetadata>,
  sensorIndex: number,
  slotNumber: number
): SensorLegendMetadata {
  if (!metadata.has(sensorIndex)) {
    metadata.set(sensorIndex, {
      sensorIndex,
      slotNumber,
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return metadata.get(sensorIndex)!;
}

/** Parses sub-type 10 snapshot: `10;eventNo;serial,slot,fw,batt,rssi|...` */
function parseSnapshotEvent(
  parts: string[],
  metadata: Map<number, SensorLegendMetadata>
): void {
  const rows = (parts[2] || "").split("|").filter(Boolean);

  for (const row of rows) {
    const [serialNumber, slotStr, fwStr, batteryStr, rssi] = row.split(",");
    const slot = Number(slotStr);
    const sensorIndex = calculateIndexFromSlotNumber(slot);

    const entry = getOrCreateEntry(metadata, sensorIndex, slot);
    entry.serialNumber = serialNumber || undefined;
    entry.firmwareVersion = fwStr || undefined;
    entry.batteryLevel = batteryStr || undefined;
    entry.rssi = rssi || undefined;
  }
}

/**
 * Extracts per-sensor metadata from the event 77 (SENSOR_CONFIGURATION_CHANGED) snapshots
 * for the drying graph legend's info badge and SENSOR INFO dialog.
 *
 * Only sub-type 10 is read: serial, firmware, battery and RSSI per sensor. Logs are applied in
 * chronological order, so the newest snapshot wins. The warning sub-types (1, 2, 3, 5, 6, 8) are
 * deliberately ignored: they are flag EDGES, and rebuilding state from them is bounded by the
 * query's limit and cannot clear a battery-weak warning at all, so the badge reads the ComBox's
 * live `sensorFlagsExt` instead.
 *
 * @param sensorEventLogs Event logs filtered to SENSOR category for the current chain
 * @returns Map of sensor index → metadata
 */
export function extractSensorLegendMetadata(
  sensorEventLogs: HyDryEventLog[]
): Map<number, SensorLegendMetadata> {
  const metadata = new Map<number, SensorLegendMetadata>();
  const event77Number = getHyDryEventNumber(HyDryEvent.SENSOR_CONFIGURATION_CHANGED);

  const relevantLogs = sensorEventLogs
    .filter(log => log.eventNumber === event77Number && log.additionalData)
    .sort((a, b) => a.eventDate.toMillis() - b.eventDate.toMillis());

  for (const log of relevantLogs) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const parts = log.additionalData!.split(";");

    if (Number(parts[0]) === 10) {
      parseSnapshotEvent(parts, metadata);
    }
  }

  return metadata;
}
